import { useEffect, Dispatch } from 'react';
import {
  getCurrentRequestType,
  initRequestState,
  IRequestState,
  RequestAction,
  requestorComments,
  requestorCommentsOfRevoke,
  usePreviousRequest,
} from '.';
import {
  useDeaRegistrations,
  IDeaEditMode,
  IDeaInfo,
  IDeaRegistration,
  IRequestDetails,
  ISaveRequestInput,
  dbRoleToRole,
  dbRoleToSecondaryRole,
  dbRoleToThirdRole,
  useRenewables,
  IRenewable,
  sortRenewablByDeaNum,
} from '../common';
import { v4 as uuidv4 } from 'uuid';
import {
  compose as lcompose,
  map as lmap,
  find as lfind,
  filter as lfilter,
  uniqBy as luniqBy,
  orderBy as lorderBy,
  eq as leq,
  negate as lnegate,
  slice,
} from 'lodash/fp';
import { assocPath, pathOr } from 'rambda';
import { map as raMap } from 'fp-ts/ReadonlyArray';
import { some, none, map } from 'fp-ts/Option';
import { flow } from 'fp-ts/function';

export function useSetRequest({
  requestState,
  dispatchRequest,
}: {
  requestState: IRequestState;
  dispatchRequest: Dispatch<RequestAction>;
}) {
  const { deaRegistrations } = useDeaRegistrations();

  const requestType = getCurrentRequestType(requestState);
  const { previousRequest } = usePreviousRequest({ requestType });

  const myDeaInfos = lcompose(
    lmap<IDeaRegistration, IDeaEditMode>(({ deaNum, role, serialNumber }) => ({
      deaNum,
      editMode: 'Select',
      msgCode: null,
      msgText: null,
      key: uuidv4(),
      role,
      serialNumber,
      selected: false,
    })),
    lorderBy(['deaNum'], ['asc']),
    luniqBy('deaNum'),
    lfilter<IDeaRegistration>(
      lcompose(lnegate(leq('ADM')), pathOr('', 'role'))
    ),
    lfilter<IDeaRegistration>({ regStatus: 'ACTIVE' }),
    lfilter<IDeaRegistration>(
      lcompose(
        lnegate(leq('CERTIFICATE_GENERATION_IN_PROCESS')),
        pathOr('', 'certStatus')
      )
    )
  )(deaRegistrations);

  const { renewables } = useRenewables();

  const renewDeaInfos = flow(
    sortRenewablByDeaNum,
    raMap<IRenewable, IDeaEditMode>(
      ({ deaNumber, role, serialNumber, expirationDt }) => ({
        deaNum: deaNumber,
        editMode: 'Select',
        msgCode: null,
        msgText: null,
        key: uuidv4(),
        role,
        serialNumber,
        selected: false,
        expDt: expirationDt,
      })
    )
    // slice(0, 1000)
  )(renewables.as);

  const existingRenewRequest = lcompose(
    (a: ISaveRequestInput) => ({
      ...a,

      deaInfos: lmap<IDeaEditMode, IDeaEditMode>(b => ({
        ...b,
        selected: !!lfind<IDeaInfo>({ deaNum: b.deaNum })(a.deaInfos),
      }))(renewDeaInfos),
    }),

    assocPath('comments', requestorComments('RENEWAL')(previousRequest)),

    (a: IRequestDetails | undefined) =>
      a === void 0 || a.reqType !== 'RENEWAL'
        ? assocPath<ISaveRequestInput>(
            'reqType',
            'RENEWAL'
          )({
            ...initRequestState.request,
            requestId: pathOr(void 0, 'requestId')(previousRequest),
          })
        : a
  )(previousRequest);

  const existingRevokeRequest = lcompose(
    (a: ISaveRequestInput) => ({
      ...a,

      deaInfos: lmap<IDeaEditMode, IDeaEditMode>(b => ({
        ...b,
        selected: !!lfind<IDeaInfo>({ deaNum: b.deaNum })(a.deaInfos),
      }))(myDeaInfos),
    }),

    assocPath('otherComments', requestorComments('REVOKE')(previousRequest)),
    assocPath(
      'comments',
      lcompose(
        requestorCommentsOfRevoke,
        requestorComments('REVOKE')
      )(previousRequest)
    ),

    (a: IRequestDetails | undefined) =>
      a === void 0 || a.reqType !== 'REVOKE'
        ? assocPath<ISaveRequestInput>(
            'reqType',
            'REVOKE'
          )({
            ...initRequestState.request,
            requestId: pathOr(void 0, 'requestId')(previousRequest),
          })
        : a
  )(previousRequest);

  const existingEnrollRequest = lcompose(
    (a: ISaveRequestInput): ISaveRequestInput => ({
      ...a,

      role: dbRoleToRole(a.role)(a.role),
      secondaryRole: dbRoleToSecondaryRole(a.role),
      thirdRole: dbRoleToThirdRole(a.role),

      deaInfos: lmap<IDeaEditMode, IDeaEditMode>(
        lcompose(
          (a: IDeaEditMode) => ({ ...a, key: uuidv4() }),
          assocPath('editMode', 'Read'),
          assocPath('msgCode', null),
          assocPath('msgText', null)
        )
      )(a.deaInfos),
    }),

    assocPath('comments', requestorComments('ENROLL')(previousRequest)),

    (a: IRequestDetails | undefined) =>
      a === void 0 || a.reqType !== 'ENROLL'
        ? {
            ...initRequestState.request,
            requestId: pathOr(void 0, 'requestId')(previousRequest),
          }
        : a
  )(previousRequest);

  // handle current request type change
  // maybe this is better to implement in requestReducer SetType

  useEffect(() => {
    trySetRequestEnroll({
      type: requestType,
      setRequestDisabled: requestState.setRequestDisabled,
    });

    trySetRequestRenew({
      type: requestType,
      setRequestDisabled: requestState.setRequestDisabled,
    });

    trySetRequestRevoke({
      type: requestType,
      setRequestDisabled: requestState.setRequestDisabled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestType, deaRegistrations, previousRequest]);

  // initial load
  // this effect has to come after
  const previousRequestType = pathOr('ENROLL', 'reqType')(previousRequest);

  useEffect(() => {
    trySetRequestEnroll({
      type: previousRequestType,
      setRequestDisabled: requestState.setRequestDisabled,
    });

    trySetRequestRenew({
      type: previousRequestType,
      setRequestDisabled: requestState.setRequestDisabled,
    });

    trySetRequestRevoke({
      type: previousRequestType,
      setRequestDisabled: requestState.setRequestDisabled,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousRequestType, deaRegistrations, renewables]);

  const trySetRequestEnroll = flow(
    ({
      type,
      setRequestDisabled,
    }: {
      type: string;
      setRequestDisabled: boolean;
    }) =>
      type === 'ENROLL' && setRequestDisabled === false ? some(type) : none,
    map(() =>
      dispatchRequest({
        type: 'SetRequest',
        payload: existingEnrollRequest,
      })
    )
  );

  const trySetRequestRenew = flow(
    ({
      type,
      setRequestDisabled,
    }: {
      type: string;
      setRequestDisabled: boolean;
    }) =>
      type === 'RENEWAL' && setRequestDisabled === false ? some(type) : none,
    map(() =>
      dispatchRequest({
        type: 'SetRequest',
        payload: existingRenewRequest,
      })
    )
  );

  const trySetRequestRevoke = flow(
    ({
      type,
      setRequestDisabled,
    }: {
      type: string;
      setRequestDisabled: boolean;
    }) =>
      type === 'REVOKE' && setRequestDisabled === false ? some(type) : none,
    map(() =>
      dispatchRequest({
        type: 'SetRequest',
        payload: existingRevokeRequest,
      })
    )
  );
}
